// src/components/CreateWorkspaceModal.js
import React, { useState, useEffect, useRef } from 'react';
import * as WorkspaceService from '../services/WorkspaceService.mjs';
import InlineBanner from './common/InlineBanner';
import Spinner from './Spinner.mjs';
import '../css/CreateWorkspaceModal.css';

/**
 * Modal for creating a new workspace.
 *
 * Props:
 *   onClose()            — called when the user dismisses the modal
 *   onCreated(workspace) — called with the new workspace after successful creation
 */
function CreateWorkspaceModal({ onClose, onCreated }) {
    const [name, setName]           = useState('');
    const [error, setError]         = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const inputRef   = useRef(null);
    const overlayRef = useRef(null);

    // Auto-focus the name input when the modal opens
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Close on Escape key
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();

        if (!trimmed) {
            setError('Please enter a workspace name.');
            return;
        }
        if (trimmed.length > 64) {
            setError('Name must be 64 characters or fewer.');
            return;
        }

        setError('');
        setIsSubmitting(true);

        try {
            // 1. Check eligibility
            const { allowed, reason } = await WorkspaceService.canCreateWorkspace();
            if (!allowed) {
                setError(reason ?? 'You are not allowed to create a new workspace.');
                return;
            }

            // 2. Create the workspace
            const workspace = await WorkspaceService.createWorkspace(trimmed);
            onCreated(workspace);
        } catch (err) {
            console.error('createWorkspace error:', err);
            setError(
                err?.response?.data?.message ?? 'Something went wrong. Please try again.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="cwm-overlay"
            ref={overlayRef}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cwm-title"
        >
            <div className="cwm-panel">
                {/* Header */}
                <div className="cwm-header">
                    <h2 className="cwm-title" id="cwm-title">Create workspace</h2>
                    <button className="cwm-close" onClick={onClose} aria-label="Close">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.5"
                             strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6"  y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <form className="cwm-form" onSubmit={handleSubmit} noValidate>
                    <div className="cwm-field">
                        <label className="cwm-label" htmlFor="cwm-name-input">
                            Workspace name
                        </label>
                        <input
                            id="cwm-name-input"
                            ref={inputRef}
                            className={`cwm-input ${error ? 'cwm-input--error' : ''}`}
                            type="text"
                            placeholder="e.g. My Team Workspace"
                            value={name}
                            maxLength={64}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (error) setError('');
                            }}
                            disabled={isSubmitting}
                        />
                        {/* Character counter */}
                        <span className={`cwm-char-count ${name.length > 56 ? 'cwm-char-count--warn' : ''}`}>
                            {name.length}/64
                        </span>
                        {error && (
                            <InlineBanner variant="danger" onDismiss={() => setError('')}>
                                {error}
                            </InlineBanner>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="cwm-footer">
                        <button
                            type="button"
                            className="qd-btn qd-btn--secondary"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="qd-btn qd-btn--primary"
                            disabled={isSubmitting || name.trim().length === 0}
                        >
                            {isSubmitting ? <Spinner inline /> : null}
                            {isSubmitting ? 'Creating…' : 'Create workspace'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateWorkspaceModal;
